import React, {
  useCallback, useContext, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  faBook, faInfo, faSignature, faTimes, faUser,
} from '@fortawesome/free-solid-svg-icons';
import {
  Badge,
  Dropdown, Modal,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Lottie from 'lottie-react';
import classNames from 'classnames';
import { CPButton } from './styles';
import { WinManagerContext } from '../../../providers';
import IconAlert from '../../blanks/common/IconAlert';
import checkAnimation from '../../../assets/lottie/animation_lmylbmm0.json';
import DateTimeLabel from '../../Controls/dates/dateTimeLabel';

export function SignButton({
  onSign, onUnsign, onGetJournal, onGetStatus, disabled,
}) {
  const [loading, setLoading] = useState(false);
  const [modalStatus, setModalStatus] = useState(null);
  const [journalData, setJournalData] = useState(null);
  const onStatusClick = useCallback(
    () => {
      setLoading(true);
      onGetStatus()
        .then((d) => setModalStatus(d))
        .finally(() => setLoading(false));
    },
    [onGetStatus],
  );
  const onJournalClick = useCallback(
    () => {
      setLoading(true);
      onGetJournal()
        .then((d) => setJournalData(d))
        .finally(() => setLoading(false));
    },
    [onGetJournal],
  );
  const { currentContainer } = useContext(WinManagerContext);
  return (
    <>
      <Dropdown>
        <Dropdown.Toggle
          as={CPButton}
          disabled={disabled}
          content="Підписи"
          icon={faSignature}
          title="Підписання, зняття підпису, перевірка статусу, перегляд журнала підписів"
          spin={loading}
        />
        <Dropdown.Menu>
          <Dropdown.Item onClick={onSign}>
            <FontAwesomeIcon icon={faSignature} size="lg" className="me-2" fixedWidth />
            Підписати
          </Dropdown.Item>
          <Dropdown.Item onClick={onUnsign}>
            <span className="fa-layers fa-fw me-2 fa-lg">
              <FontAwesomeIcon icon={faSignature} className="me-2" transform="right-8" />
              <FontAwesomeIcon icon={faTimes} transform="grow-12 left-8" className="text-danger" />
            </span>
            Зняти підпис
          </Dropdown.Item>
          <Dropdown.Item onClick={onStatusClick}>
            <FontAwesomeIcon icon={faInfo} size="lg" className="me-2" fixedWidth />
            Поточний статус
          </Dropdown.Item>
          <Dropdown.Item onClick={onJournalClick}>
            <FontAwesomeIcon icon={faBook} size="lg" className="me-2" fixedWidth />
            Переглянути журал підписів
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      <Modal container={currentContainer} show={!!modalStatus} onHide={() => setModalStatus(null)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Поточний статус підписання документу
          </Modal.Title>
        </Modal.Header>
        {!!modalStatus && (
          <>
            <Modal.Body>
              {modalStatus.map((sr, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className="d-flex align-items-center mb-4 gap-2" key={i}>
                  <div className="bg-200 rounded-circle text-primary border-300 fs-3 icon-item icon-item-lg fw-bold position-relative" title="№ групи">
                    {sr.level}
                    {sr.action && (
                      <div className="position-absolute">
                        <Lottie animationData={checkAnimation} loop={false} />
                      </div>
                    )}
                  </div>
                  <div className="flex-fill d-flex1">
                    <h5>{sr.level_name}</h5>
                    <div className="d-flex align-items-center">
                      {sr.action
                        ? (
                          <div className="mx-2">
                            Підписано
                            {' '}
                            {sr.user.repr}
                            <DateTimeLabel
                              value={sr.action_date}
                              displayFormat="long"
                              className="mx-0 small text-muted"
                            />
                          </div>
                        )
                        : (
                          <div className="mx-2">
                            Не підписано або підпис було знято
                          </div>
                        )}
                    </div>
                  </div>
                </div>

              ))}
            </Modal.Body>
            {!modalStatus.filter((sr) => sr.action).length && (
              <IconAlert variant="info" className="mb-0">
                Документ ще не піписано жодною посадовою особою
              </IconAlert>
            )}
            {!!modalStatus.filter((sr) => sr.level === 1 && sr.action).length && (
              <IconAlert variant="success" className="mb-0">
                Документ затверджено
              </IconAlert>
            )}
          </>
        )}
      </Modal>
      <Modal container={currentContainer} show={!!journalData} onHide={() => setJournalData(null)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            Журнал підписів
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!!journalData && journalData.map((jr) => (
            <div key={jr.id} className={classNames('border-bottom pb-2 mb-2', { 'text-success-emphasis': jr.action, 'text-danger-emphasis': !jr.action })}>
              <h5>
                {jr.action ? 'Встановлено підпис' : 'Підпис знято'}
              </h5>
              <div className="d-flex gap-2">
                <DateTimeLabel value={jr.action_date} displayFormat="long" />
                <div className="fw-bold">
                  <FontAwesomeIcon icon={faUser} className="mx-1" />
                  {jr.user.repr}
                </div>
                <div>
                  <Badge pill bg="secondary" className="mx-1">{jr.level}</Badge>
                  {jr.level_name}
                </div>
              </div>
            </div>
          ))}
        </Modal.Body>
      </Modal>
    </>
  );
}

SignButton.propTypes = {
  onSign: PropTypes.func.isRequired,
  onUnsign: PropTypes.func.isRequired,
  onGetJournal: PropTypes.func.isRequired,
  onGetStatus: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

SignButton.defaultProps = {
  disabled: false,
};

export default SignButton;
